import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import {
  SubscriptionUsageMetric,
  SubscriptionUsageModel,
  SubscriptionService,
} from "src/app/Services/subscription.service";
import { setCoinPurchaseReturnUrl } from "src/app/shared/utils/coin-purchase-return.util";
import { ToastrService } from "ngx-toastr";
import { TrialStateService } from "src/app/Services/trial-state.service";

export interface SubscriptionUsageDialogData {
  usage?: SubscriptionUsageModel | null;
}

@Component({
  selector: "app-subscription-usage-dialog",
  templateUrl: "./subscription-usage-dialog.component.html",
  styleUrls: ["./subscription-usage-dialog.component.scss"],
})
export class SubscriptionUsageDialogComponent implements OnInit {
  usage: SubscriptionUsageModel | null = null;
  isLoading = true;
  loadError = false;

  cancellingTrial = false;

  constructor(
    private subscriptionService: SubscriptionService,
    private router: Router,
    public dialogRef: MatDialogRef<SubscriptionUsageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SubscriptionUsageDialogData,
    private trialStateService: TrialStateService,
    private toastr: ToastrService
  ) {}

  get isTrial(): boolean {
    return this.usage?.planTier === "trial";
  }

  cancelTrial(): void {
    if (this.cancellingTrial) {
      return;
    }
    const confirmed = window.confirm(
      "Cancel your Starter trial? You won't be charged, and creating guides will stop straight away. Guides you've already made are kept."
    );
    if (!confirmed) {
      return;
    }
    this.cancellingTrial = true;
    this.trialStateService.cancelTrial().subscribe({
      next: () => {
        this.cancellingTrial = false;
        this.toastr.success("Your Starter trial has been cancelled. You won't be charged.", "Trial cancelled");
        this.dialogRef.close({ trialCancelled: true });
      },
      error: (err) => {
        this.cancellingTrial = false;
        this.toastr.error(
          err?.error?.message || "We couldn't cancel your trial. Please try again or contact support.",
          "Cancel failed"
        );
      },
    });
  }

  ngOnInit(): void {
    if (this.data?.usage) {
      this.usage = this.data.usage;
      this.isLoading = false;
      return;
    }
    this.loadUsage();
  }

  loadUsage(attempt = 0): void {
    this.isLoading = true;
    this.loadError = false;
    this.subscriptionService.getUsage().subscribe({
      next: (usage) => {
        this.usage = usage;
        this.isLoading = false;
      },
      error: (err) => {
        // First open can race with other subscription GETs or hit a transient 429/5xx;
        // auto-retry once or twice before showing the error (manual Try again still works).
        const status = err?.status as number | undefined;
        const retryable =
          status == null || status === 0 || status === 429 || status >= 500;
        if (attempt < 2 && retryable) {
          window.setTimeout(() => this.loadUsage(attempt + 1), 350 * (attempt + 1));
          return;
        }
        this.loadError = true;
        this.isLoading = false;
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  goToPricing(): void {
    this.dialogRef.close();
    this.router.navigate(["/pricing"], {
      queryParams: this.isTrial ? { trial: "reminder" } : {},
    });
  }

  goToPurchaseGuideSlots(): void {
    const returnUrl = this.router.url;
    setCoinPurchaseReturnUrl(returnUrl);
    this.dialogRef.close();
    this.router.navigate(["/pricing"], {
      queryParams: { buyGuides: "1", returnUrl },
      fragment: "guide-topups",
    });
  }

  canPurchaseGuideSlots(): boolean {
    if (this.isTrial) return false;
    const planId = (this.usage?.planId || "").toLowerCase();
    return planId === "starter" || planId === "growth";
  }

  goToPurchaseCoins(): void {
    const returnUrl = this.router.url;
    setCoinPurchaseReturnUrl(returnUrl);
    this.dialogRef.close();
    this.router.navigate(["/pricing"], {
      queryParams: { buyCoins: "1", returnUrl },
      fragment: "ai-coins",
    });
  }

  canPurchaseCoins(): boolean {
    if (this.isTrial) return false;
    const planId = (this.usage?.planId || "").toLowerCase();
    return (
      planId === "creator" ||
      planId === "starter" ||
      planId === "growth" ||
      planId === "enterprise"
    );
  }

  metricBarWidth(metric: SubscriptionUsageMetric): string {
    if (metric.unlimited || metric.limit <= 0) {
      return "0%";
    }
    return `${Math.min(100, metric.percentUsed || 0)}%`;
  }

  metricLevel(metric: SubscriptionUsageMetric): "ok" | "warn" | "critical" | "unlimited" | "info" {
    if (metric.notIncluded) {
      return "info";
    }
    if (metric.key === "trial_days") {
      const left = metric.limit - metric.used;
      return left <= 1 ? "critical" : left <= 4 ? "warn" : "ok";
    }
    if (metric.unlimited) {
      return "unlimited";
    }
    if (metric.key === "max_steps") {
      return "info";
    }
    const percent = metric.percentUsed || 0;
    if (percent >= 100) {
      return "critical";
    }
    if (percent >= 80) {
      return "warn";
    }
    return "ok";
  }

  metricIcon(metric: SubscriptionUsageMetric): string {
    const key = (metric.key || "").toLowerCase();
    if (metric.notIncluded) {
      return "lock";
    }
    if (key === "trial_days") {
      return "hourglass_top";
    }
    if (key.includes("coin")) {
      return "toll";
    }
    if (key.includes("view")) {
      return "visibility";
    }
    if (key.includes("flow") || key.includes("guide") || key.includes("publish")) {
      return "auto_stories";
    }
    if (key.includes("step")) {
      return "linear_scale";
    }
    if (key.includes("storage") || key.includes("upload")) {
      return "cloud_upload";
    }
    return "insights";
  }

  metricPrimaryValue(metric: SubscriptionUsageMetric): string {
    if (metric.notIncluded) {
      return "—";
    }
    if (metric.key === "max_steps") {
      return metric.unlimited ? "∞" : String(metric.limit);
    }
    if (metric.key === "ai_coins" && metric.label.toLowerCase().includes("top-up")) {
      return String(metric.limit);
    }
    if (metric.unlimited) {
      return String(metric.used);
    }
    if (metric.limit <= 0) {
      return "0";
    }
    return String(Math.max(0, metric.remaining ?? metric.limit - metric.used));
  }

  metricPrimaryHint(metric: SubscriptionUsageMetric): string {
    if (metric.notIncluded) {
      return "not in trial";
    }
    if (metric.key === "trial_days") {
      return "days left";
    }
    if (metric.key === "max_steps") {
      return metric.unlimited ? "steps per guide" : `${metric.unit} per guide`;
    }
    if (metric.key === "ai_coins" && metric.label.toLowerCase().includes("top-up")) {
      return "available";
    }
    if (metric.unlimited) {
      return "used · unlimited";
    }
    if (metric.limit <= 0) {
      return "available";
    }
    return "left";
  }

  metricSecondaryLabel(metric: SubscriptionUsageMetric): string {
    if (metric.notIncluded || metric.key === "trial_days") {
      return metric.detail || "";
    }
    if (metric.key === "trial_guides") {
      return `${metric.used} used of ${metric.limit} · ${metric.detail || "manual creation only"}`;
    }
    if (metric.key === "max_steps") {
      return metric.unlimited
        ? "No step cap on this plan"
        : `Limit of ${metric.limit} ${metric.unit}`;
    }
    if (metric.key === "ai_coins") {
      if (metric.limit <= 0) {
        return metric.detail || "No coins available";
      }
      if (metric.label.toLowerCase().includes("top-up")) {
        return "Purchased top-up balance";
      }
      return `${metric.used} used of ${metric.limit}`;
    }
    if (metric.unlimited) {
      return "No monthly limit";
    }
    return `${metric.used} used of ${metric.limit} ${metric.unit}`;
  }

  metricStatusLabel(metric: SubscriptionUsageMetric): string {
    if (metric.notIncluded) {
      return "Upgrade to unlock";
    }
    if (metric.key === "trial_days") {
      const level = this.metricLevel(metric);
      return level === "ok" ? "Trial active" : "Ending soon";
    }
    const level = this.metricLevel(metric);
    if (level === "unlimited") {
      return "Unlimited";
    }
    if (level === "info") {
      return "Cap";
    }
    if (level === "critical") {
      return "Exceeded";
    }
    if (level === "warn") {
      return "Near limit";
    }
    return "On track";
  }

  showMetricBar(metric: SubscriptionUsageMetric): boolean {
    return !metric.notIncluded && !metric.unlimited && metric.limit > 0 && metric.key !== "max_steps";
  }

  isActiveStatus(status: string | null | undefined): boolean {
    const value = (status || "").trim().toUpperCase();
    return value === "ACTIVE" || value === "APPROVED";
  }

  statusLabel(status: string | null | undefined): string {
    const value = (status || "").trim().toUpperCase();
    if (value === "NONE" || !value) {
      return "No paid plan";
    }
    if ((value === "ACTIVE" || value === "APPROVED") && this.isTrial) {
      return "Free trial";
    }
    if (value === "ACTIVE" || value === "APPROVED") {
      return "Success";
    }
    return status || "Unknown";
  }
}
